import { useLazyQuery, useMutation } from '@apollo/client/react';
import { type CSSProperties, useContext, useState } from 'react';
import {
	type CloneProjectMutation,
	type CloneProjectMutationVariables,
	type ProjectsQuery,
	type ProjectsQueryVariables,
	type ProjectDetailsPageQuery,
	type ProjectDetailsPageQueryVariables,
	type ProjectFieldsFragment
} from '../../__generated__/graphql-client-types';
import { FEATURE_FLAGS } from '../../constants/feature-flags';
import { PROJECTS_NOTIFICATION_TIMEOUT } from '../../constants/general';
import { ACCOUNT_PROJECTS_LINK } from '../../constants/links';
import { EXAMPLE_PROJECT_ID } from '../../constants/project/general';
import { MOBILE_LEFT_OFFSET, MOBILE_SIDEBAR_LEFT_OFFSET, NUM_REQUIRED_PRODUCTS } from '../../constants/project/projects-tour';
import { StepIdsEnum } from '../../constants/project/projects-tour-enums';
import { ProjectsTourContext } from '../../contexts/projects/projects-tour/projects-tour.context';
import { buildGTMProjectInteractionTracking } from '../../helpers/analytics/gtm/gtm-utils.helper';
import { windowIsExtraLarge } from '../../helpers/breakpoints/breakpoint.helpers';
import { isProject } from '../../helpers/project/project.helper';
import { getTourSteps, getHighlightStyles, getTourBoxPosition, isManagementStep } from '../../helpers/project-tour/project-tour.helper';
import { PROJECT_DETAILS_PAGE } from '../../queries/project/project-details.queries';
import { FETCH_HEADER_PROJECTS } from '../../queries/project/project-header.queries';
import { CLONE_PROJECT } from '../../queries/project/project.queries';
import { type CurrentStepData, type TourStep } from '../../types/project-tour.types';
import { useTrackEvent } from '../analytics/analytics.hooks';
import { useFeature } from '../features/features.hooks';
import { useNavigation } from '../navigation/navigation.hooks';
import { useNotification } from '../notification/notification.hooks';
import { useProjectId } from '../project-details';

const SPOTLIT_STEPS = [StepIdsEnum.GROUP, StepIdsEnum.ORDERS_RETURNS, StepIdsEnum.COMMUNICATION, StepIdsEnum.PORTFOLIO];

export const useProjectsTour = () => {
	const isProjectsSharingEnabled = useFeature<boolean>(FEATURE_FLAGS.PROJECTS_SHARING);
	const track = useTrackEvent();
	const navigate = useNavigation();
	const projectId = useProjectId();
	const { notifyDanger } = useNotification();
	const {
		currentStepRef,
		currentStepRefMobile,
		currentStep: { currentStepIndex, currentStepId: activeStepId },
		setCurrentStep
	} = useContext(ProjectsTourContext);
	const flattenedSteps = getTourSteps({ isSharingEnabled: isProjectsSharingEnabled }).flat();

	const [popUpStyle, setPopUpStyle] = useState<CSSProperties>({});
	const [spotlightStyle, setSpotlightStyle] = useState<CSSProperties>({ display: 'none' });
	const [highlightStyles, setHighlightStyles] = useState<CSSProperties>({
		top: 0,
		left: 0,
		width: 0,
		height: 0
	});

	const [cloneProject] = useMutation<CloneProjectMutation, CloneProjectMutationVariables>(CLONE_PROJECT);
	const [findExampleProject] = useLazyQuery<ProjectsQuery, ProjectsQueryVariables>(FETCH_HEADER_PROJECTS);
	const [getProjectById] = useLazyQuery<ProjectDetailsPageQuery, ProjectDetailsPageQueryVariables>(PROJECT_DETAILS_PAGE);

	const onError = (): void => notifyDanger('Sorry, something went wrong, refresh the page and try again.', PROJECTS_NOTIFICATION_TIMEOUT);

	const cloneExampleProject = (): void => {
		cloneProject({
			variables: {
				input: {
					projectId: EXAMPLE_PROJECT_ID
				}
			}
		})
			.then((res) => {
				if (res.data?.cloneProject.__typename === 'CloneProject') {
					const clonedProjectId = res.data?.cloneProject.id;
					navigate(`${ACCOUNT_PROJECTS_LINK}/${clonedProjectId}/feed?tour=1`);
				}
			})
			.catch(() => onError());
	};

	const getEligibleTourProject = (fetchedProjects: ProjectFieldsFragment[]): void => {
		const exampleProject = fetchedProjects.find((project) => project.name.includes('Example Project'));
		const id = projectId || exampleProject?.id;
		if (id) {
			void getProjectById({
				variables: {
					projectId: id
				}
			}).then((res) => {
				const foundProject = res.data?.projectById;
				if (isProject(foundProject)) {
					const firstGroup = foundProject.groups[0];
					if (firstGroup?.pending?.length === NUM_REQUIRED_PRODUCTS) {
						navigate(`${ACCOUNT_PROJECTS_LINK}/${id}/feed?tour=1`);
					} else {
						cloneExampleProject();
					}
				} else {
					cloneExampleProject();
				}
			});
		} else {
			cloneExampleProject();
		}
	};

	const startTour = (): void => {
		if (projectId) {
			track(buildGTMProjectInteractionTracking('projects-tour', 'start_tour_project_details'));
		} else {
			track(buildGTMProjectInteractionTracking('projects-tour', 'start_tour_about_page'));
		}

		void findExampleProject({
			variables: {
				input: {
					paging: {
						pageSize: 20
					},
					includeShoppingListGroups: false
				}
			}
		}).then((res) => {
			const fetchedProjects = res?.data?.projects?.projects;
			if (fetchedProjects?.length) {
				getEligibleTourProject(fetchedProjects);
			} else {
				cloneExampleProject();
			}
		});
	};

	const handleNext = (): void => {
		if (currentStepIndex < flattenedSteps.length - 1) {
			setCurrentStep((prev) => ({
				currentStepIndex: prev.currentStepIndex + 1,
				currentStepId: flattenedSteps[prev.currentStepIndex + 1].id
			}));
			track(buildGTMProjectInteractionTracking('projects-tour', 'go_forward'));
		}

		// use second to last step to track tour completion
		if (activeStepId === StepIdsEnum.PORTFOLIO) {
			track(buildGTMProjectInteractionTracking('projects-tour', 'tour_completed'));
		}
	};

	const handlePrev = (): void => {
		if (currentStepIndex > 0) {
			setCurrentStep({ currentStepIndex: currentStepIndex - 1, currentStepId: flattenedSteps[currentStepIndex - 1].id });
			track(buildGTMProjectInteractionTracking('projects-tour', 'go_back'));
		}
	};

	const exitTour = (shouldGoBack: boolean = false): void => {
		if (shouldGoBack) {
			navigate(-1);
		} else {
			navigate('?');
		}
		document.body.classList.remove('overflow-y-hidden');
		setCurrentStep({ currentStepIndex: -1, currentStepId: null });
		track(buildGTMProjectInteractionTracking('projects-tour', 'exit_tour'));
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const moveHighlight = (
		currentStepId: StepIdsEnum,
		currentIndex: number,
		currentStepRef: React.RefObject<HTMLDivElement> | null,
		spotlightRef: React.RefObject<HTMLDivElement> | null
	): void => {
		const highlightRef = SPOTLIT_STEPS.includes(currentStepId) ? spotlightRef : currentStepRef;
		if (currentIndex >= 0 && highlightRef?.current) {
			const targetRect = highlightRef.current.getBoundingClientRect();
			setHighlightStyles(getHighlightStyles(currentStepId, targetRect, window.scrollY, window.scrollX));
		} else {
			setHighlightStyles({ display: 'none' });
		}
	};

	const moveTourBox = (currentStepData: CurrentStepData, step: TourStep, currentRef: React.RefObject<HTMLDivElement> | null): void => {
		const { currentStepIndex: currentIndex, currentStepId } = currentStepData;
		if (currentIndex === -1) return;

		if (currentRef && currentRef.current) {
			const rect = currentRef.current.getBoundingClientRect();
			// accurately position tooltip if page is scrolled
			const scrollTop = window.scrollY;
			const scrollLeft = window.scrollX;
			const isMobileView = !windowIsExtraLarge();

			const isOnManagementStep = isManagementStep(currentStepId);
			const isSearchStep = currentStepId === StepIdsEnum.SEARCH_FROM_PROJECTS;
			const scrollAlignment = isMobileView && isOnManagementStep ? 'start' : 'center';
			const mobileOffset = isOnManagementStep ? MOBILE_SIDEBAR_LEFT_OFFSET : MOBILE_LEFT_OFFSET;
			const leftOffset = isMobileView ? mobileOffset : step.leftOffset ?? 0;
			const topOffset = isMobileView ? step.mobileTopOffset ?? 0 : step.topOffset ?? 0;

			setPopUpStyle(getTourBoxPosition(rect, step.verticalPosition, topOffset, leftOffset, scrollTop, scrollLeft));

			currentRef.current.scrollIntoView({
				behavior: 'smooth',
				block: scrollAlignment,
				inline: scrollAlignment
			});

			// On 375 x 667 size screens, the search component is cut off so we scroll down to make it visible
			if (isMobileView && isSearchStep) {
				window.scrollBy({
					top: 100
				});
			}

			if (currentIndex > 0) {
				setSpotlightStyle({
					top: rect.top + scrollTop,
					left: rect.left + scrollLeft,
					width: rect.width,
					height: rect.height
				});
			} else {
				setSpotlightStyle({ display: 'none' });
			}
		}
	};

	const getProductCardRef = (
		isTourActive: boolean,
		isMobile: boolean,
		currentStep: StepIdsEnum
	): React.RefObject<HTMLDivElement> | null | undefined => {
		const finalRef = isMobile ? currentStepRefMobile : currentStepRef;
		return isTourActive && activeStepId === currentStep ? finalRef : undefined;
	};

	return {
		handleNext,
		handlePrev,
		exitTour,
		startTour,
		moveTourBox,
		moveHighlight,
		getProductCardRef,
		popUpStyle,
		spotlightStyle,
		highlightStyles
	};
};
